import { Select } from '@mantine/core'
import type { SharedTypes } from '@semios/app-platform-banyan-route-definitions'
import { getIdAndEmitterTypeFromZoneEmitterTypeKey } from 'App/Map/_utils/irrigationZoneEmitterTypeKeyUtil'
import { PanelDetailsContext } from 'App/Map/PanelDetails/PanelDetails'
import { translate } from 'i18n/i18n'
import { useContext, useMemo } from 'react'
import { colors } from 'settings/colors'
import { hubConnectorSettings } from 'settings/hubConnectorSettings'
import { SharedSettings } from 'settings/SharedSettings'
import { fieldAssetStore } from 'stores/fieldAssetStore/fieldAssetStore'
import type { TProperty } from 'stores/fieldAssetStore/helperTypes'
import { selectedFieldAssetsStore } from 'stores/selectedFieldAssetsStore/selectedFieldAssetsStore'
import { getIrrigationZoneEmitterExternalVendorNames } from 'utils/getIrrigationZoneEmitterExternalVendorNames'
import type { TSelectItemWithExternalVendors } from 'utils/makeExternalVendorSelectProps'
import { makeExternalVendorSelectProps } from 'utils/makeExternalVendorSelectProps'
import { makeNameEmitterTypeLabel } from 'utils/makeNameEmitterTypeLabel'
import { mantineSelectSearchFilterProp } from 'utils/mantineSelectSearchFilterProp'
import { mapApiEmitterTypeToLabel } from 'utils/mapApiEmitterTypeToLabel'
import { setSelectedFieldAsset } from 'utils/setSelectedFieldAsset/setSelectedFieldAsset'
import { sortByKey } from 'utils/sortByKey'
import { useSelectClickToggle } from 'utils/useSelectClickToggle'

type SelectDataItem = TSelectItemWithExternalVendors & {
  name?: string
}

type SelectDataGroup = {
  group: string
  items: SelectDataItem[]
}

export const DropdownSelectorIrrigationZoneEmitter = ({
  isPressureSensor = false,
}: {
  isPressureSensor?: boolean
}) => {
  const { selectedIrrigationZoneEmitter } = selectedFieldAssetsStore.useSelector((s) => ({
    selectedIrrigationZoneEmitter: s.irrigationZoneEmitter,
  }))

  const { containerWidth } = useContext(PanelDetailsContext)
  const selectClickToggle = useSelectClickToggle()

  const { properties } = fieldAssetStore.useSelector((s) => ({
    properties: s?.properties ?? [],
  }))

  const data = useMemo<SelectDataGroup[]>(() => {
    return Object.values(properties ?? {})
      .sort(sortByKey('propertyName'))
      .flatMap((p: TProperty) => {
        if (!p.irrigationZoneEmitters) return []

        const items = Object.entries(p.irrigationZoneEmitters)
          .filter(([, zone]) => {
            const { emitterType } = getIdAndEmitterTypeFromZoneEmitterTypeKey(zone.irrigationZoneEmitterId)

            if (isPressureSensor)
              return !!Object.values(p.points ?? {})?.some(
                (pt) =>
                  pt.configuration.irrigationEmitterTypesAvailable?.includes(emitterType) &&
                  pt.irrigationEmitterZoneIds?.some((ptz) => {
                    return ptz === zone.irrigationZoneEmitterId
                  }),
              )
            else return true
          })
          .sort((a, b) => {
            const labelA = mapApiEmitterTypeToLabel(a[1].emitterType)
            const labelB = mapApiEmitterTypeToLabel(b[1].emitterType)

            if (labelA < labelB) return -1

            if (labelA > labelB) return 1

            return 0
          })
          .map(([irrigationZoneId, irrigationZoneEmitter]) => ({
            value: irrigationZoneId,
            label: makeNameEmitterTypeLabel({
              name: irrigationZoneEmitter.name,
              emitterType: irrigationZoneEmitter.emitterType,
            }),
            externalVendorNames: getIrrigationZoneEmitterExternalVendorNames({
              irrigationZoneEmitter,
              points: Object.values(p.points ?? {}),
            }),

            name: irrigationZoneEmitter.name,
          }))
          .sort(sortByKey('name'))

        if (!items.length) return []

        return [{ group: p.propertyName, items }]
      })
  }, [selectedIrrigationZoneEmitter, properties])

  if (!data.length) return null

  const { selectProps, inputPaddingRight } = makeExternalVendorSelectProps({
    data,
    value: selectedIrrigationZoneEmitter,
  })

  return (
    // can prevent the section from collapsing by passing stopPropagation
    <div onClick={(e) => e.stopPropagation()}>
      <Select
        {...selectClickToggle}
        clearable={false}
        css={{
          width: containerWidth < 600 ? 200 : Math.min(280, Math.round(containerWidth / 3)),
          border: `1px solid ${colors.midnight}`,
          borderRadius: 3,
        }}
        data={data}
        {...selectProps}
        filter={mantineSelectSearchFilterProp}
        onChange={(newSelectedIrrigationZoneEmitter) => {
          if (newSelectedIrrigationZoneEmitter === null) return

          return setSelectedFieldAsset({
            irrigationZoneEmitter:
              newSelectedIrrigationZoneEmitter as SharedTypes.IrrigationScheduler.TIrrigationZoneEmitterId,
          })
        }}
        placeholder={translate.phrases.banyanApp('Select an Irrigation Zone')}
        searchable
        size="xs"
        spellCheck="false"
        classNames={SharedSettings.MANTINE_SELECT_UNDERLINE_SEPARATOR_CLASSNAMES}
        styles={{
          ...SharedSettings.MANTINE_SELECT_RIGHT_ICON_CHANGER,
          ...SharedSettings.MANTINE_SELECT_UNDERLINE_SEPARATOR_STYLES,
          dropdown: { marginTop: -6, fontWeight: 'normal' },
          input: {
            ...SharedSettings.MANTINE_SELECT_RIGHT_ICON_CHANGER.input,
            paddingRight: inputPaddingRight,
          },
        }}
        value={selectedIrrigationZoneEmitter?.toString()}
        comboboxProps={{
          withinPortal: true,
          keepMounted: false,
          zIndex: hubConnectorSettings.zIndexForDropdownSelectorInStackemCharts,
        }}
      />
    </div>
  )
}
